// Display formatting for the portfolio dashboard's EUR analytics: money in the
// user's selected currency (EUR while rates load), percentages and dates in the
// app's number format. Chart axes and tooltips need plain strings, which the
// `<Money>` component can't give them.

import { useMemo } from "react";
import { useCurrency } from "../contexts/currency/index.js";
import { useNumberFormat } from "../contexts/numberFormat/index.js";

export interface PortfolioFormat {
  /** EUR minor units → "€1,234" in the selected currency. */
  money: (eurMinor: number, opts?: Intl.NumberFormatOptions) => string;
  /** Same, abbreviated for axes ("€1.2K"). */
  compact: (eurMinor: number) => string;
  /** A percentage given in percent points (12.3 → "12.3%"); `signed` adds +/−. */
  pct: (points: number, opts?: { signed?: boolean }) => string;
}

export function usePortfolioFormat(): PortfolioFormat {
  const { selected, convert } = useCurrency();
  const { formatMoney, formatNumber } = useNumberFormat();
  return useMemo(() => {
    // Decide once whether rates are ready so one chart never mixes currencies.
    const ready = convert(0, "EUR") !== null;
    const currency = ready ? selected : "EUR";
    const money: PortfolioFormat["money"] = (eurMinor, opts) =>
      formatMoney(ready ? (convert(eurMinor, "EUR") ?? eurMinor) : eurMinor, currency, opts);
    return {
      money,
      compact: (eurMinor) => money(eurMinor, { notation: "compact", maximumFractionDigits: 1 }),
      pct: (points, opts) =>
        formatNumber(points / 100, {
          style: "percent",
          maximumFractionDigits: 1,
          ...(opts?.signed ? { signDisplay: "exceptZero" as const } : {}),
        }),
    };
  }, [selected, convert, formatMoney, formatNumber]);
}

/** `YYYY-MM-DD` → "26 Jun" (the day is a UTC calendar day, so it is formatted in UTC). */
export function formatDay(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, { day: "numeric", month: "short", timeZone: "UTC" });
}

/** `YYYY-MM` → "Jun 26". */
export function formatMonth(month: string): string {
  return new Date(`${month}-01T00:00:00Z`).toLocaleDateString(undefined, {
    month: "short",
    year: "2-digit",
    timeZone: "UTC",
  });
}
